import { Fragment, useMemo } from 'react'

import { faEllipsisStrokeVertical } from '@fortawesome/pro-light-svg-icons/faEllipsisStrokeVertical'
import { useNotifyClickOutside } from '@space-core/hooks'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'
import clsx from 'clsx'

import { ActionBtn, ButtonIcon, CommunityText, CustomIcon, DropdownItemsBox } from './styles'
import type { CollapseItemActionsProps } from './types'

const CollapseItemActions = ({
  handleActiveCollapse,
  actions,
  classNames,
  dropdownIcon = faEllipsisStrokeVertical
}: CollapseItemActionsProps) => {
  const { theme } = useThemeContext()

  const { ref: contextMenuItemsParentRef, isVisible, setIsVisible } = useNotifyClickOutside()

  const handlerClick = (event: React.MouseEvent<HTMLButtonElement>, callBackAction) => {
    event?.stopPropagation()
    setIsVisible(false)
    callBackAction()
  }

  const handleActionHeader = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    handleActiveCollapse?.('false')
    setIsVisible((current) => !current)
  }

  const shouldShowComponent = useMemo(() => {
    if (actions.length < 1) {
      return false
    }

    return actions.some((action) => action.isVisibleItem)
  }, [actions])

  if (!shouldShowComponent) {
    return null
  }

  return (
    <div
      className={clsx(classNames, '_d-flex _flex-column _align-items-end _position-relative')}
      ref={contextMenuItemsParentRef}>
      <ActionBtn
        data-test='context-menu-icon'
        className='hot-button _d-flex _align-items-center _border-0'
        onClickCapture={handleActionHeader}>
        <span className='_sr-only'>context menu icon</span>
        <ButtonIcon icon={dropdownIcon} className='_text-gray-900' />
      </ActionBtn>

      {isVisible && (
        <div className='_d-flex _flex-column _align-items-end'>
          <DropdownItemsBox
            theme={theme}
            className='_d-flex _flex-column _position-fixed _shadow-sm _rounded-lg _mt-2 _ml-2'>
            {actions.map(({ callBack, label, icon, isVisibleItem }, index) => (
              <Fragment key={index}>
                {isVisibleItem && (
                  <button
                    key={index}
                    onClickCapture={(event: React.MouseEvent<HTMLButtonElement>) =>
                      handlerClick(event, callBack)
                    }
                    className='_d-flex _align-items-center _border-0 hot-button _px-4 _py-5'>
                    {icon && (
                      <CustomIcon
                        icon={icon}
                        data-test='menu-icon-edit'
                        className='_text-gray-900'
                      />
                    )}

                    <CommunityText
                      className={clsx('_text-left', {
                        '_ml-4': Boolean(icon)
                      })}>
                      {label}
                    </CommunityText>
                  </button>
                )}
              </Fragment>
            ))}
          </DropdownItemsBox>
        </div>
      )}
    </div>
  )
}

export default CollapseItemActions
